import React from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  TouchableOpacity, 
  Platform,
  Dimensions,
  ScrollView 
} from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

interface PaymentModalProps {
  children: React.ReactNode;
  title: string;
  onClose: () => void;
  width?: number;
  maxHeight?: string;
  showCloseButton?: boolean;
  disableOverlayClose?: boolean;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  children,
  title,
  onClose,
  width = 600,
  maxHeight = '90%',
  showCloseButton = true,
  disableOverlayClose = false,
}) => {
  const windowHeight = Dimensions.get('window').height;
  let computedMaxHeight: number | undefined = undefined;
  if (typeof maxHeight === 'string' && maxHeight.endsWith('%')) {
    const pct = Number(maxHeight.replace('%', '')) || 90;
    computedMaxHeight = Math.round((pct / 100) * windowHeight);
  } else if (typeof maxHeight === 'number') {
    computedMaxHeight = maxHeight;
  }
  const content = (
    <View style={styles.wrap}>
      <TouchableOpacity activeOpacity={1} onPress={disableOverlayClose ? undefined : onClose} style={styles.overlay} />
      <View style={[
        styles.modal,
        { 
          width: Math.min(Dimensions.get('window').width - 32, width),
          ...(computedMaxHeight ? { maxHeight: computedMaxHeight } : {}),
        }
      ]}>
        <View style={styles.header}>
          <Text style={styles.title}>{title}</Text>
          {showCloseButton && (
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              {(() => {
                const Icon = (RESIDENTS_SVG_COMPONENTS as any).X_MARK;
                return Icon ? <Icon width={24} height={24} color={RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY} /> : null;
              })()}
            </TouchableOpacity>
          )}
        </View>
        {children}
      </View>
    </View>
  );

  if (Platform.OS === 'web') {
    try {
      const g: any = globalThis as any;
      const ReactDOM = g?.require ? g.require('react-dom') : require('react-dom');
      const target = g?.document?.body;
      if (ReactDOM?.createPortal && target) {
        return ReactDOM.createPortal(content, target);
      }
    } catch {}
  }

  return content;
};

export const PaymentModalContent: React.FC<{ children: React.ReactNode; style?: any }> = ({ 
  children, 
  style 
}) => (
  <ScrollView style={[styles.content, style]} contentContainerStyle={styles.contentContainer}>
    {children}
  </ScrollView>
);

export const PaymentModalFooter: React.FC<{ 
  children: React.ReactNode; 
  spaceBetween?: boolean;
  style?: any;
}> = ({ 
  children, 
  spaceBetween = false,
  style 
}) => (
  <View style={[styles.footer, spaceBetween && styles.footerSpaceBetween, style]}>
    {children}
  </View>
);

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web'
      ? ({ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 10000 } as any)
      : {}),
  },
  overlay: {
    position: (Platform.OS === 'web' ? 'fixed' : 'absolute') as any,
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 1,
  },
  modal: {
    borderRadius: 12,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    zIndex: 2000,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
  },
  title: {
    fontSize: 20,
    fontWeight: '900',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  closeButton: {
    padding: 4,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  contentContainer: {
    paddingBottom: 20,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
  },
  footerSpaceBetween: {
    justifyContent: 'space-between',
  },
});

export default PaymentModal;
